import {
  Button,
  Input,
  Menu,
  MenuButton,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  __css,
  tokens,
} from '@fluentui/react-components';
import { Dismiss16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import type { DocumentFilters } from './documents-api';
import { phaseOptionLabel } from './metadata-fields';
import {
  DISCIPLINES,
  DOCUMENT_TYPES,
  PHASE_TYPES,
  type Discipline,
  type DocumentType,
  type PhaseType,
} from './naming-model';

/** Name of the radio group of a chip's menu. */
const CHOICE = 'choice';

const useStyles = __css({"bar":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"},"search":{"Bh6795r":"fqerorx","xawz":"ffq3mkc","D305fb":"fjg76ag"}});

interface ChipProps<T extends string> {
  /** Names the filter: "Discipline". */
  label: string;
  value: T | undefined;
  options: readonly { value: T; label: string }[];
  onChange: (value: T | undefined) => void;
}

/**
 * A filter chip: a button naming the filter (and its value once chosen) that opens a menu of
 * radio choices. The chosen value is written in words, so the state never relies on colour.
 */
function FilterChip<T extends string>({ label, value, options, onChange }: ChipProps<T>) {
  const { t } = useTranslation('documents');
  const chosen = options.find((option) => option.value === value);
  return (
    <Menu
      checkedValues={{ [CHOICE]: [value ?? ''] }}
      onCheckedValueChange={(_event, data) => {
        const next = data.checkedItems[0];
        onChange(options.find((option) => option.value === next)?.value);
      }}
    >
      <MenuTrigger disableButtonEnhancement>
        <MenuButton size="small" shape="circular" appearance={chosen ? 'primary' : 'outline'}>
          {chosen ? t('filters.chosen', { filter: label, value: chosen.label }) : label}
        </MenuButton>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItemRadio name={CHOICE} value="">
            {t('filters.all')}
          </MenuItemRadio>
          {options.map((option) => (
            <MenuItemRadio key={option.value} name={CHOICE} value={option.value}>
              {option.label}
            </MenuItemRadio>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

export interface DocumentFiltersBarProps {
  filters: DocumentFilters;
  onChange: (patch: Partial<DocumentFilters>) => void;
  /** The text of the search box (the list reads it after a pause in typing). */
  text: string;
  onTextChange: (text: string) => void;
  onClear: () => void;
}

/** Search box and filter chips (discipline, phase, type) of the files list. */
export function DocumentFiltersBar({
  filters,
  onChange,
  text,
  onTextChange,
  onClear,
}: DocumentFiltersBarProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const active =
    filters.discipline !== undefined ||
    filters.phaseType !== undefined ||
    filters.docType !== undefined ||
    text.trim() !== '';
  return (
    <div className={styles.bar} role="search" aria-label={t('filters.label')}>
      <Input
        className={styles.search}
        type="search"
        aria-label={t('filters.search')}
        placeholder={t('filters.searchPlaceholder')}
        value={text}
        onChange={(_event, data) => {
          onTextChange(data.value);
        }}
      />
      <FilterChip<Discipline>
        label={t('metadata.discipline')}
        value={filters.discipline}
        options={DISCIPLINES.map((value) => ({
          value,
          label: t(`ui:components.disciplines.${value}`),
        }))}
        onChange={(discipline) => {
          onChange({ discipline });
        }}
      />
      <FilterChip<PhaseType>
        label={t('metadata.phase')}
        value={filters.phaseType}
        options={PHASE_TYPES.map((value) => ({ value, label: phaseOptionLabel(t, value) }))}
        onChange={(phaseType) => {
          onChange({ phaseType });
        }}
      />
      <FilterChip<DocumentType>
        label={t('metadata.docType')}
        value={filters.docType}
        options={DOCUMENT_TYPES.map((value) => ({ value, label: t(`docTypes.${value}`) }))}
        onChange={(docType) => {
          onChange({ docType });
        }}
      />
      {active ? (
        <Button appearance="subtle" size="small" icon={<Dismiss16Regular />} onClick={onClear}>
          {t('filters.clear')}
        </Button>
      ) : null}
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/document-filters.griffel.css";